import { CenterFeedPlacement, FeedProperty } from '@/types/models'
import { isNonEmptyArray, isNumber } from '../../../utils/checks'

const hasRepeatOrPattern = (feedProperty: FeedProperty): boolean =>
  isNumber(feedProperty.inFeed?.repeatEvery) ||
  isNonEmptyArray(feedProperty.inFeed?.repeatPattern)

const useRepeatPattern = (
  repeatInterval: number,
  feedProperty: FeedProperty
) => {
  if (
    feedProperty?.inFeed &&
    isNonEmptyArray(feedProperty.inFeed?.repeatPattern) &&
    feedProperty.inFeed?.repeatPattern
  ) {
    const { repeatPattern } = feedProperty.inFeed
    const normalizedIndex = repeatInterval % repeatPattern?.length
    return repeatPattern[normalizedIndex]
  }
  return undefined
}

export const AddItemsToSubFeed = (
  feed: CenterFeedPlacement[],
  subFeed: Partial<CenterFeedPlacement[]> | [],
  feedProperty: FeedProperty,
  positionStart: number
) => {
  if (
    !isNonEmptyArray(subFeed) ||
    !hasRepeatOrPattern(feedProperty) ||
    !subFeed
  ) {
    return feed
  }

  const parentFeed: CenterFeedPlacement[] = feed
  const itemsToBeAdded = subFeed

  if (isNonEmptyArray(feedProperty.inFeed?.repeatPattern)) {
    const feedItems = addItemsWithRepeatPattern({
      feed: parentFeed,
      subFeed: itemsToBeAdded,
      feedProperty
    })
      .filter(Boolean)
      .flat()
      .sort((a, b) => (a.feedPosition as number) - (b.feedPosition as number))
      .map((item, index) => {
        return { ...item, feedPosition: index + positionStart }
      }) as CenterFeedPlacement[]
    return feedItems
  }

  const feedItems = addItemsToSubFeedWithRepeatEvery({
    feed: parentFeed,
    subFeed: itemsToBeAdded,
    feedProperty,
    positionStart
  })
    .filter(Boolean)
    .flat()
    // eslint-disable-next-line
    // @ts-ignore-next-line
    .sort((a, b) => (a.feedPosition as number) - (b.feedPosition as number))
    .map((item, index) => {
      return { ...item, feedPosition: index + positionStart }
    }) as CenterFeedPlacement[]
  return feedItems
}

export const addItemsWithRepeatPattern = ({
  feed,
  subFeed,
  feedProperty
}: {
  feed: CenterFeedPlacement[]
  subFeed: Partial<CenterFeedPlacement[]> | []
  feedProperty: FeedProperty
}) => {
  if (
    !isNonEmptyArray(feedProperty.inFeed?.repeatPattern) ||
    !isNonEmptyArray(subFeed)
  ) {
    return feed
  }

  const parentFeed: CenterFeedPlacement[] = feed
  const itemsToBeAdded = subFeed

  const { feedType } = feedProperty
  let repeatPatternIndex = 0
  let iterationIndex = 0
  let repeatPattern = useRepeatPattern(repeatPatternIndex, feedProperty)

  if (!repeatPattern || !isNumber(repeatPattern.repeatEvery)) {
    return feed
  }
  const modifiedParentFeed = parentFeed
    .sort((a, b) => {
      return a.feedPosition - b.feedPosition
    })
    .reduce((acc, item, i) => {
      repeatPattern = useRepeatPattern(repeatPatternIndex, feedProperty)

      if (
        repeatPattern &&
        iterationIndex > 0 &&
        ((iterationIndex as number) + 1) % repeatPattern.repeatEvery === 0 &&
        isNonEmptyArray(itemsToBeAdded)
      ) {
        item.feedPosition = i
        const itemsToInsert = itemsToBeAdded
          .splice(0, repeatPattern?.itemsToAdd as number)
          .map((item, positionIndex) => {
            return {
              ...item,
              feedPosition: i + positionIndex,
              placedInFeed: feedType
            }
          })

        repeatPatternIndex++
        iterationIndex = 0
        return [...acc, ...itemsToInsert, { ...item }]
      }
      iterationIndex++
      return [...acc, { ...item, feedPosition: acc.length + 1 }]
    }, [])
  return modifiedParentFeed
}

export const addItemsToSubFeedWithRepeatEvery = ({
  feed,
  subFeed,
  feedProperty,
  positionStart
}: {
  feed: CenterFeedPlacement[]
  subFeed: Partial<CenterFeedPlacement[]> | []
  feedProperty: FeedProperty
  positionStart: number
}) => {
  const parentFeed: CenterFeedPlacement[] = feed
  const { feedType } = feedProperty
  const itemsToBeAdded = subFeed
  const totalItems = isNumber(feedProperty.maxItems)
    ? (feedProperty.maxItems as number) + feed.length
    : subFeed.length + feed.length

  let totalCentersAdded = 0
  const repeatEvery = feedProperty.inFeed?.repeatEvery as number
  const feedItems: (CenterFeedPlacement | undefined)[] = []

  for (let i = 0; i < totalItems; i++) {
    if (!isNonEmptyArray(parentFeed)) {
      i = totalItems
    }

    totalCentersAdded++
    if (
      (i + 1) % repeatEvery === 0 &&
      totalCentersAdded < totalItems &&
      isNonEmptyArray(itemsToBeAdded)
    ) {
      feedItems.push({
        ...(itemsToBeAdded.shift() as CenterFeedPlacement),
        feedPosition: i + positionStart || (i as number),
        placedInFeed: feedType
      })
    } else {
      feedItems.push({
        ...parentFeed.shift(),
        feedPosition: i + positionStart || (i as number)
      } as CenterFeedPlacement)
    }
  }
  return feedItems
}
